import type { OnboardingPreview, OnboardingPreviewDeal, OnboardingPreviewLead } from "../services/OnboardingPreviewService";
import type { HomeDepotStore, WalmartStore } from "./storeUtils";
import { formatHomeDepotStoreId } from "./storeUtils";

/**
 * "Is this ZIP covered?" for the deals-first onboarding.
 *
 * Plans are built on Walmart and HD home stores, and a store starts being
 * scanned as soon as a member picks it, so a ZIP is covered when a Walmart or
 * HD store sits within COVERAGE_RADIUS_MILES (the public deal preview's
 * radius). A ZIP whose store preview already shows local deals is always
 * covered, even if the store catalogs failed to load. Anything we cannot
 * decide (no coordinates, a catalog that didn't load) is "unknown", which the
 * page treats as covered, so an empty-but-covered ZIP is never labelled
 * "we don't scan stores near you".
 */
export const COVERAGE_RADIUS_MILES = 35;
/** Store steps list stores up to this far; "Choose a plan anyway" needs one. */
export const STORE_PICKER_RADIUS_MILES = 100;

export type CoverageState = "pending" | "covered" | "none" | "unknown";

export interface NearestStore {
  retailer: "walmart" | "home_depot";
  storeId: string;
  /** "Gainesville, FL" (empty when the catalog has no city). */
  place: string;
  /** Store ZIP, to look the city up when the catalog has none (most Walmart rows). */
  postalCode: string;
  miles: number;
}

export interface Coords {
  lat: number;
  long: number;
}

export function distanceMiles(a: Coords, b: Coords): number {
  const R = 3959;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.long - a.long) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** "1101 S Military Trl, Deerfield Beach, FL 33442" → "Deerfield Beach, FL". */
export function placeFromAddress(address: string): string {
  const parts = address.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length < 2) return "";
  const city = parts[1];
  const state = (parts[2] ?? "").split(/\s+/)[0] ?? "";
  if (/^\d/.test(city)) return "";
  return state && /^[A-Za-z]{2}$/.test(state) ? `${city}, ${state.toUpperCase()}` : city;
}

const place = (city: string | undefined, state: string | undefined, address: string) => {
  const c = city?.trim();
  const s = state?.trim();
  if (c) return s ? `${c}, ${s}` : c;
  return placeFromAddress(address);
};

/** The closest Walmart and HD stores to the ZIP, at any distance. */
export function nearestStores(
  coords: Coords | null,
  walmart: WalmartStore[] | null | undefined,
  homeDepot: HomeDepotStore[] | null | undefined,
  limit = 3,
): NearestStore[] {
  if (!coords) return [];
  const out: NearestStore[] = [];
  for (const store of walmart ?? []) {
    if (!store?.store_id || !store.geo || (!store.geo.lat && !store.geo.long)) continue;
    out.push({
      retailer: "walmart",
      storeId: store.store_id,
      place: place(store.city, store.state, store.address ?? ""),
      postalCode: (store.postal_code ?? "").trim(),
      miles: distanceMiles(coords, store.geo),
    });
  }
  for (const store of homeDepot ?? []) {
    const lat = parseFloat(store?.latitude);
    const long = parseFloat(store?.longitude);
    if (!store?.store_number || Number.isNaN(lat) || Number.isNaN(long) || (!lat && !long)) continue;
    out.push({
      retailer: "home_depot",
      storeId: formatHomeDepotStoreId(store.store_number),
      place: place(store.city, store.state, store.address_1 ?? ""),
      postalCode: (store.zip ?? store.postal_code ?? "").trim(),
      miles: distanceMiles(coords, { lat, long }),
    });
  }
  out.sort((a, b) => a.miles - b.miles);
  return out.slice(0, limit);
}

export function previewHasLocalDeals(preview: OnboardingPreview | null | undefined): boolean {
  return !!preview && (preview.mode === "own" || preview.mode === "nearby") && preview.stores.length > 0 && preview.deals.length > 0;
}

export function detectCoverage(input: {
  preview: OnboardingPreview | null | undefined;
  /** The ZIP was geocoded and both catalogs finished loading (or failed). */
  resolved: boolean;
  coords: Coords | null;
  catalogsLoaded: boolean;
  nearest: NearestStore[];
}): CoverageState {
  if (previewHasLocalDeals(input.preview)) return "covered";
  if (!input.resolved) return "pending";
  if (!input.coords || !input.catalogsLoaded) return "unknown";
  const closest = input.nearest[0];
  return closest && closest.miles <= COVERAGE_RADIUS_MILES ? "covered" : "none";
}

/** One card on the onboarding pages, from a local deal, a lead or an example. */
export interface OnbDeal {
  key: string;
  name: string;
  image: string;
  price: number;
  wasPrice: number;
  discountPercent: number;
  /** "Walmart #1517 · 3 mi", "Walmart · Los Angeles, CA"; empty to hide. */
  storeLabel: string;
  /** Shelf details, present only on the deals shown in full. */
  aisle?: string;
  quantity?: number;
}

export function dealsFromPreview(preview: OnboardingPreview | null | undefined, labelOverride?: string): OnbDeal[] {
  if (!preview) return [];
  if (preview.mode === "own" || preview.mode === "nearby") {
    return preview.deals.map((deal: OnboardingPreviewDeal, index) => ({
      key: `${deal.storeId}-${index}-${deal.name}`,
      name: deal.name,
      image: deal.image,
      price: deal.price,
      wasPrice: deal.wasPrice,
      discountPercent: deal.discountPercent,
      storeLabel: labelOverride !== undefined ? labelOverride : `Walmart #${deal.storeId} · ${Math.max(1, Math.round(deal.distanceMiles))} mi`,
      aisle: deal.aisle,
      quantity: deal.quantity,
    }));
  }
  if (preview.mode === "leads") {
    return preview.leads.map((lead: OnboardingPreviewLead, index) => ({
      key: `lead-${index}-${lead.title}`,
      name: lead.title,
      image: lead.image,
      price: lead.price,
      wasPrice: lead.wasPrice,
      discountPercent: lead.discountPercent,
      storeLabel: "",
    }));
  }
  return [];
}

/** "Los Angeles, CA" for an example preview's first store, or "" for leads/none. */
export function previewPlace(preview: OnboardingPreview | null | undefined): string {
  if (!preview || (preview.mode !== "own" && preview.mode !== "nearby")) return "";
  for (const store of preview.stores) {
    const found = placeFromAddress(store.address);
    if (found) return found;
  }
  return "";
}

export function formatDealPrice(value: number): string {
  return `$${value.toFixed(2)}`;
}
